// frontend/src/components/CreateBot.js
import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, TextField, Button, Select, MenuItem, 
  FormControl, InputLabel, Chip, Avatar, 
  FormControlLabel, Radio, RadioGroup, Switch, FormGroup 
} from '@mui/material';
import { getCurrentIdentity } from '../userIdentity';

const DEFAULT_AVATAR = '/assets/default-bot.png';
const genresOptions = ['18+', 'Флирт', 'Ролевые игры', 'Сюжет', 'Общение', 'Фантазия', 'Романтика', 'Приключения'];

const CreateBot = () => {
  const [avatar, setAvatar] = useState(DEFAULT_AVATAR);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [genres, setGenres] = useState([]);
  const [gender, setGender] = useState('');
  const [greeting, setGreeting] = useState('');
  const [appearance, setAppearance] = useState('');
  const [plot, setPlot] = useState('');
  const [character, setCharacter] = useState('');
  const [personality, setPersonality] = useState('');
  const [details, setDetails] = useState('');
  const [isDndMode, setIsDndMode] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);
  
  const [errors, setErrors] = useState({});
  const [userId, setUserId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false); // Защита от двойного клика

  useEffect(() => {
    let mounted = true;

    const loadIdentity = async () => {
      try {
        const identity = await getCurrentIdentity();
        if (mounted && identity) {
          setUserId(identity.userId);
        }
      } catch (err) {
        console.error('Ошибка загрузки identity:', err);
      }
    };

    loadIdentity();

    return () => {
      mounted = false;
    };
  }, []);

  const handleAvatarChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatar(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (name.length > 20) newErrors.name = 'Максимум 20 символов';
    if (description.length > 200) newErrors.description = 'Максимум 200 символов';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    
    if (!userId) {
      alert('Ошибка: Пользователь не идентифицирован');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/bots', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          avatar,
          name,
          description,
          genres: genres.join(','),
          gender,
          greeting,
          appearance,
          plot,
          character,
          personality,
          details,
          isDndMode: isDndMode ? 1 : 0,
          isPrivate: isPrivate ? 1 : 0
        }),
      });

      if (response.ok) {
        alert('Бот успешно создан!');
        // Сброс формы
        setAvatar(DEFAULT_AVATAR);
        setName('');
        setDescription('');
        setGenres([]);
        setGender('');
        setGreeting('');
        setAppearance('');
        setPlot('');
        setCharacter('');
        setPersonality('');
        setDetails('');
        setIsDndMode(false);
        setIsPrivate(false);
      } else {
        alert('Ошибка создания бота на сервере');
      }
    } catch (error) {
      alert('Не удалось подключиться к серверу');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenresChange = (event) => {
    const { target: { value } } = event;
    const selected = typeof value === 'string' ? value.split(',') : value;
    setGenres(selected.slice(0, 3));
  };

  return (
    // translate="no" спасает от багов с дублированием элементов из-за Google Translate
    <Box sx={{ p: 2, overflowY: 'auto' }} translate="no">
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', textAlign: 'center' }}>
        Создай бота
      </Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <label htmlFor="avatar-upload">
          <Avatar src={avatar} sx={{ width: 100, height: 100, cursor: 'pointer', border: '2px solid #333' }} />
          <input id="avatar-upload" type="file" accept="image/*" onChange={handleAvatarChange} style={{ display: 'none' }} />
        </label>
      </Box>
      <Typography variant="body2" sx={{ textAlign: 'center', mb: 2, fontStyle: 'italic' }}>
        Добавь аватарку боту
      </Typography>

      <TextField
        label="Имя бота"
        value={name}
        onChange={(e) => setName(e.target.value)}
        fullWidth
        margin="normal"
        error={!!errors.name}
        helperText={errors.name}
        autoComplete="off"
        InputLabelProps={{ shrink: true }}
        sx={{ '& .MuiFormLabel-root': { fontStyle: 'italic' } }}
      />
      
      <TextField
        label="Описание бота"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        fullWidth
        multiline
        rows={3}
        margin="normal"
        error={!!errors.description}
        helperText={errors.description}
        autoComplete="off"
        InputLabelProps={{ shrink: true }}
        sx={{ '& .MuiFormLabel-root': { fontStyle: 'italic' } }}
      />

      <Box sx={{ mt: 2, mb: 1, p: 2, border: '1px solid #555', borderRadius: 2, bgcolor: isDndMode ? 'rgba(156, 39, 176, 0.1)' : 'transparent' }}>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={isDndMode}
                onChange={(e) => setIsDndMode(e.target.checked)}
                color="secondary"
              />
            }
            label={
              <Typography sx={{ fontWeight: 'bold', fontStyle: 'italic', color: isDndMode ? '#9c27b0' : 'inherit' }}>
                Включить D&D Режим
              </Typography>
            }
          />
        </FormGroup>
        {isDndMode && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            В этом режиме бот будет выступать как ДМ, а не персонаж
          </Typography>
        )}
      </Box>

      <Box sx={{ mb: 1, p: 2, border: '1px solid #555', borderRadius: 2, bgcolor: isPrivate ? 'rgba(33, 150, 243, 0.1)' : 'transparent' }}>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={isPrivate}
                onChange={(e) => setIsPrivate(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography sx={{ fontWeight: 'bold', fontStyle: 'italic', color: isPrivate ? '#2196f3' : 'inherit' }}>
                Бот только для меня (приватный)
              </Typography>
            }
          />
        </FormGroup>
        {isPrivate && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Этот бот не появится списке ботов, чат с ним будет только у тебя
          </Typography>
        )}
      </Box>

      <FormControl fullWidth margin="normal">
        <InputLabel shrink sx={{ fontStyle: 'italic' }}>Жанр (до 3)</InputLabel>
        <Select
          multiple
          value={genres}
          onChange={handleGenresChange}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => <Chip key={value} label={value} />)}
            </Box>
          )}
        >
          {genresOptions.map((genre) => (
            <MenuItem key={genre} value={genre}>
              {genre}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth margin="normal">
        <InputLabel shrink sx={{ fontStyle: 'italic' }}>Выбери пол боту</InputLabel>
        <RadioGroup value={gender} onChange={(e) => setGender(e.target.value)} row sx={{ mt: 2 }}>
          <FormControlLabel value="Мужчина" control={<Radio />} label="Мужчина" />
          <FormControlLabel value="Женщина" control={<Radio />} label="Женщина" />
        </RadioGroup>
      </FormControl>

      <TextField label="Введи приветственное сообщение" value={greeting} onChange={(e) => setGreeting(e.target.value)} fullWidth multiline rows={2} margin="normal" autoComplete="off" InputLabelProps={{ shrink: true }} sx={{ '& .MuiFormLabel-root': { fontStyle: 'italic' } }} />
      <TextField label="Внешность" value={appearance} onChange={(e) => setAppearance(e.target.value)} fullWidth multiline rows={2} margin="normal" autoComplete="off" InputLabelProps={{ shrink: true }} sx={{ '& .MuiFormLabel-root': { fontStyle: 'italic' } }} />
      <TextField label="Сюжет" value={plot} onChange={(e) => setPlot(e.target.value)} fullWidth multiline rows={3} margin="normal" autoComplete="off" InputLabelProps={{ shrink: true }} sx={{ '& .MuiFormLabel-root': { fontStyle: 'italic' } }} />
      <TextField label="Характер" value={character} onChange={(e) => setCharacter(e.target.value)} fullWidth multiline rows={2} margin="normal" autoComplete="off" InputLabelProps={{ shrink: true }} sx={{ '& .MuiFormLabel-root': { fontStyle: 'italic' } }} />
      <TextField label="Личность" value={personality} onChange={(e) => setPersonality(e.target.value)} fullWidth multiline rows={2} margin="normal" autoComplete="off" InputLabelProps={{ shrink: true }} sx={{ '& .MuiFormLabel-root': { fontStyle: 'italic' } }} />
      <TextField label="Детали" value={details} onChange={(e) => setDetails(e.target.value)} fullWidth multiline rows={3} margin="normal" autoComplete="off" InputLabelProps={{ shrink: true }} sx={{ '& .MuiFormLabel-root': { fontStyle: 'italic' } }} />

      <Button 
        variant="contained" 
        fullWidth 
        onClick={handleSubmit} 
        disabled={!userId || isSubmitting} 
        sx={{ mt: 2, borderRadius: '20px' }}
      >
        {isSubmitting ? 'Создание...' : 'Создать бота'}
      </Button>
    </Box>
  );
};

export default CreateBot;